import { useState } from 'react';
import { execActions, apiErrText as errText, type UnsignSubcontractResult } from '../lib/api';

/**
 * 撤销签署 / Revert signature — 改改 9/20 (fb_3267bf3b).
 *
 * 「合同已下载但并未签订，就可以申请执行预付款」. The advance cert was already gated on status 'signed';
 * what went wrong is that 记录已签署 was fired with a file that was not a chopped paper — the system
 * cannot see a chop in pixels. RecordSignatureForm now makes that an explicit attestation, and this is
 * the other half: a premature signature has to be undoable IN THE APP. Until today Eric reverted it by
 * hand-running a script against prod.
 *
 * Deliberately dumb and shared: it owns nothing but its own open/reason/busy state, and the PARENT does
 * the cache invalidation through `onDone`, because 「验收标准与合同」 and 「执行与结算」 hold different
 * query keys. No shared state between the two call sites.
 */
const REVERT_ROLES = new Set(['gm', 'budget', 'agent']);

export default function RevertSignature({ scId, role, onDone, disabled }: {
  scId: string;
  /** me.account.role — the control renders for the roles the route accepts (the server re-checks). */
  role: string | null | undefined;
  /**
   * Parent-owned cache invalidation, awaited before the panel closes. It receives the server's result so
   * the caller can report what ACTUALLY happened — `package_stage` comes back `{from: 12, to: 12}` when the
   * package never reached st.13, and a toast that claims a rollback that did not occur is a lie (rig 9/20).
   */
  onDone: (result: UnsignSubcontractResult) => void | Promise<void>;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // hooks first, then the role gate — never return before the useState calls.
  // 预算/GM only, mirroring the 免除 gate on the compliance file: 采购/商务 may read a contractual record
  // but may not lift one. 'agent' matches the server's own allow-list (POST /subcontracts/:scId/unsign)
  // so the browser rig can drive this control end-to-end.
  if (!REVERT_ROLES.has(role ?? '')) return null;

  async function submit() {
    const r = reason.trim();
    if (!r) return;
    setBusy(true); setErr(null);
    try {
      const result = await execActions.unsignSubcontract(scId, { reason: r });
      await onDone(result);
      setOpen(false); setReason('');
    } catch (e) {
      // the 409 `detail` says exactly which downstream record blocks the revert
      setErr(errText(e));
    } finally { setBusy(false); }
  }

  if (!open) {
    return (
      <button className="small" disabled={disabled || busy} onClick={() => { setOpen(true); setErr(null); }}
        title="合同其实尚未盖章签字？撤销签署，合同回到草稿状态 / recorded by mistake? revert the subcontract to draft">
        ↩ 撤销签署 / Revert signature
      </button>
    );
  }

  return (
    <div className="card" style={{ marginTop: 6, borderColor: 'var(--warn)', width: '100%' }}>
      <div className="small" style={{ fontWeight: 600, marginBottom: 4 }}>撤销签署 / Revert signature</div>
      <div className="small muted" style={{ marginBottom: 6 }}>
        将撤销以下内容 / this will undo:
        <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
          <li>分包合同回到「草稿」状态 / the subcontract returns to draft</li>
          <li>预付款凭证与预付款台账将被删除 / the advance cert and the 预付款 ledger are deleted</li>
          <li>采购包回到第 12 阶段 / the package returns to stage 12</li>
          <li>已上传的签章扫描件保留存档 / the uploaded scan is KEPT on file as evidence</li>
        </ul>
      </div>
      <div className="field" style={{ marginBottom: 6 }}>
        <label>撤销原因（必填）/ reason (required)</label>
        <input value={reason} onChange={(e) => setReason(e.target.value)} disabled={busy}
          placeholder="例如：上传的是下载版合同，纸质尚未盖章 / e.g. the upload was the downloaded draft — the paper is not chopped yet" />
      </div>
      <div className="row-wrap" style={{ gap: 8 }}>
        <button className="btn-warn small" disabled={busy || !reason.trim()} onClick={() => void submit()}>
          {busy ? '撤销中… / reverting…' : '确认撤销 / Confirm revert'}
        </button>
        <button className="small" disabled={busy} onClick={() => { setOpen(false); setReason(''); setErr(null); }}>取消 / Cancel</button>
      </div>
      {err && <div className="small" style={{ color: 'var(--bad)', marginTop: 6 }}>{err}</div>}
    </div>
  );
}
